/**
 * Envoltorio de campo de formulario accesible: genera un `id` y lo asocia al `<label>` real
 * (docs/FASE-1.md §6 "Accesibilidad mínima: label real por campo"). `children` es una función de
 * render para poder inyectar `id`/`aria-describedby` en el control sin clonar elementos.
 */
import { useId, type ReactElement, type ReactNode } from "react";

export interface FieldRenderProps {
  id: string;
  "aria-describedby"?: string;
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: (props: FieldRenderProps) => ReactNode;
}): ReactElement {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter((value): value is string => Boolean(value)).join(" ") || undefined;

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {children(describedBy ? { id, "aria-describedby": describedBy } : { id })}
      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
